import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import type { ReactNode } from 'react'

/**
 * Radix dropdown with Hermes' chrome.
 *
 * Radix is used strictly for behaviour — roving focus, typeahead, escape and
 * outside-click, the aria wiring. Every visual decision below is ours, which is
 * the reason for an unstyled primitive instead of a component library with an
 * opinion of its own.
 *
 * Overlays are the one place with a shadow and no border: a border would put a
 * second separator against the panel hairlines behind it.
 */
export function Menu({
  trigger,
  label,
  children,
  align = 'end',
}: {
  trigger: ReactNode
  /** Accessible name for the trigger button. */
  label: string
  children: ReactNode
  align?: 'start' | 'center' | 'end'
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={label}
        className="flex items-center gap-1.5 border border-line-strong px-2 py-0.5 text-micro uppercase tracking-label text-fg-secondary hover:bg-inset hover:text-fg data-[state=open]:bg-inset data-[state=open]:text-fg"
        style={{ transition: 'color var(--dur-fast) var(--ease-swift), background-color var(--dur-fast) var(--ease-swift)' }}
      >
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={4}
          className="z-50 min-w-36 bg-raised p-1 text-sm shadow-overlay"
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

/** Item de ação. `danger` marca o que encerra sessão ou destrói algo. */
export function MenuItem({
  onSelect,
  danger = false,
  children,
}: {
  onSelect: () => void
  danger?: boolean
  children: ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={`flex cursor-default items-center gap-2 px-2 py-1 outline-none select-none data-[highlighted]:bg-inset ${
        danger ? 'text-crit' : 'text-fg-secondary data-[highlighted]:text-fg'
      }`}
    >
      {children}
    </DropdownMenu.Item>
  )
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-line-subtle" />
}

/** Cabeçalho não interativo — identifica de quem é o menu. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2 py-1 text-micro uppercase tracking-label text-fg-muted">
      {children}
    </DropdownMenu.Label>
  )
}

export function MenuRadioGroup({
  value,
  onValueChange,
  children,
}: {
  value: string
  onValueChange: (value: string) => void
  children: ReactNode
}) {
  return (
    <DropdownMenu.RadioGroup value={value} onValueChange={onValueChange}>
      {children}
    </DropdownMenu.RadioGroup>
  )
}

export function MenuRadioItem({ value, children }: { value: string; children: ReactNode }) {
  return (
    <DropdownMenu.RadioItem
      value={value}
      className="flex cursor-default items-center gap-2 px-2 py-1 text-fg-secondary outline-none select-none data-[highlighted]:bg-inset data-[highlighted]:text-fg data-[state=checked]:text-fg"
    >
      {/* A 1px rule instead of a check glyph: same information, no icon noise. */}
      <span className="flex w-2 justify-center" aria-hidden="true">
        <DropdownMenu.ItemIndicator>
          <span className="block h-3 w-0.5 bg-accent" />
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </DropdownMenu.RadioItem>
  )
}
